import { createElement, lazy, type ComponentType } from "react";

type PageModule = { default: ComponentType };

export type PreloadableComponent = ComponentType & {
  /** Loads the component's code; resolves immediately for eager components. */
  preload: () => Promise<unknown>;
};

/**
 * React.lazy plus a preload hook. Once the module has loaded, the component
 * renders directly instead of through lazy(), so a page whose code was
 * preloaded before mount never shows its Suspense fallback (which would
 * replace prerendered markup and cause a layout shift).
 */
export function lazyWithPreload(
  factory: () => Promise<PageModule>
): PreloadableComponent {
  let loaded: ComponentType | undefined;
  let pending: Promise<PageModule> | undefined;

  const load = () => {
    pending ??= factory().then(
      module => {
        loaded = module.default;
        return module;
      },
      error => {
        pending = undefined;
        throw error;
      }
    );
    return pending;
  };

  const LazyComponent = lazy(load);
  const Preloadable = (props: object) =>
    createElement(loaded ?? LazyComponent, props);
  return Object.assign(Preloadable, { preload: load });
}

export function eager(component: ComponentType): PreloadableComponent {
  return Object.assign(component, { preload: () => Promise.resolve() });
}
